import { useRef } from "react";
import { motion, useInView } from "motion/react";
import * as Icons from "lucide-react";
import { siteData } from "../../data/mock";

const spring = { type: "spring", damping: 80, stiffness: 400, mass: 1 };

/* ========== Imagine Section ========== */
export const ImagineSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const { imagine } = siteData;

  return (
    <section ref={ref} className="py-20 md:py-28 bg-white overflow-hidden">
      <div className="max-w-[1200px] mx-auto px-6 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left - Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ ...spring, delay: 0.2 }}
            className="relative"
          >
            <div className="rounded-3xl overflow-hidden">
              <img
                src={imagine.image}
                alt="Happy customers"
                className="w-full h-[320px] md:h-[400px] object-cover"
              />
            </div>
          </motion.div>

          {/* Right - Quotes */}
          <div>
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ ...spring, delay: 0.1 }}
              className="text-[#0A0A0A] leading-tight tracking-tight mb-10"
              style={{ fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 500 }}
            >
              {imagine.headline}
            </motion.h2>

            <div className="space-y-6">
              {imagine.quotes.map((quote, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 30 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ ...spring, delay: 0.25 + i * 0.12 }}
                  className="relative pl-6 border-l-[3px] border-[#008055]"
                >
                  <p className="text-[#0A0A0A] text-lg font-medium italic mb-1">
                    “{quote.text}”
                  </p>
                  <p className="text-[#757478] text-sm">— {quote.context}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ========== Industries Section ========== */
const IndustryCard = ({ item, index, inView }) => {
  const IconComponent = Icons[item.icon] || Icons.Star;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ ...spring, delay: 0.1 + index * 0.08 }}
      className="group bg-white rounded-2xl p-6 border border-[#E6E4EB] hover:border-[#008055]/30 hover:shadow-lg hover:shadow-[#008055]/5 text-center cursor-default"
      style={{ transition: "border-color 0.3s ease, box-shadow 0.3s ease" }}
    >
      <div className="w-14 h-14 rounded-2xl bg-[#F0EDF5] flex items-center justify-center mx-auto mb-4 group-hover:bg-[#008055]/10" style={{ transition: "background-color 0.3s ease" }}>
        <IconComponent size={24} className="text-[#3C3B3D] group-hover:text-[#008055]" style={{ transition: "color 0.3s ease" }} />
      </div>
      <h3 className="text-[#0A0A0A] text-base font-medium mb-1">{item.name}</h3>
      <p className="text-[#757478] text-sm leading-relaxed">{item.benefit}</p>
    </motion.div>
  );
};

export const IndustriesSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const { industries } = siteData;

  return (
    <section ref={ref} id="industries" className="py-20 md:py-28 bg-[#FAFAFA]">
      <div className="max-w-[1200px] mx-auto px-6 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ ...spring }}
          className="text-center mb-14"
        >
          <span className="inline-block text-[#008055] text-sm font-medium bg-[#008055]/8 rounded-full px-4 py-1.5 mb-4">
            Industries
          </span>
          <h2
            className="text-[#0A0A0A] leading-tight tracking-tight"
            style={{ fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 500 }}
          >
            {industries.headline}
            <span className="text-[#008055]">{industries.headlineAccent}</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {industries.items.map((item, i) => (
            <IndustryCard key={i} item={item} index={i} inView={inView} />
          ))}
        </div>
      </div>
    </section>
  );
};

/* ========== White Label Section ========== */
export const WhiteLabelSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const { whiteLabel } = siteData;

  return (
    <section ref={ref} className="py-20 md:py-28 bg-white">
      <div className="max-w-[1200px] mx-auto px-6 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left content */}
          <div>
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ ...spring, delay: 0.1 }}
              className="text-[#0A0A0A] leading-tight tracking-tight mb-6"
              style={{ fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 500 }}
            >
              {whiteLabel.headline}
              <span className="text-[#008055]">{whiteLabel.headlineAccent}</span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ ...spring, delay: 0.2 }}
              className="text-[#3C3B3D] text-lg leading-relaxed mb-8"
            >
              {whiteLabel.description}
            </motion.p>

            <div className="grid grid-cols-2 gap-4">
              {whiteLabel.points.map((point, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ ...spring, delay: 0.3 + i * 0.08 }}
                  className="flex items-center gap-3 bg-[#F0EDF5] rounded-xl p-4"
                >
                  <Icons.Check size={18} className="text-[#008055] flex-shrink-0" />
                  <span className="text-[#0A0A0A] text-sm font-medium">{point}</span>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Right - Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ ...spring, delay: 0.3 }}
            className="relative"
          >
            <div className="rounded-3xl overflow-hidden shadow-lg">
              <img
                src={whiteLabel.image}
                alt="White label app"
                className="w-full h-[320px] md:h-[420px] object-cover"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
